import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import API from "../../../services/api";
import "./hostel.css";

export default function HostelRooms() {
  const { hostelId } = useParams();
  const navigate = useNavigate();
  const school = JSON.parse(localStorage.getItem("schoolAdmin"));

  const [rooms, setRooms] = useState([]);
  const [roomNumber, setRoomNumber] = useState("");
  const [totalBeds, setTotalBeds] = useState(1);
  const [monthlyFee, setMonthlyFee] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const roomSummary = rooms.reduce(
    (summary, room) => {
      const totalBedsCount = Number(room.total_beds || 0);
      const occupiedBedsCount = Number(room.occupied_beds || 0);
      const monthlyFeeValue = Number(room.monthly_fee || 0);

      return {
        rooms: summary.rooms + 1,
        beds: summary.beds + totalBedsCount,
        occupied: summary.occupied + occupiedBedsCount,
        free: summary.free + Number(room.free_beds || Math.max(totalBedsCount - occupiedBedsCount, 0)),
        feeTotal: summary.feeTotal + monthlyFeeValue,
      };
    },
    { rooms: 0, beds: 0, occupied: 0, free: 0, feeTotal: 0 }
  );

  const averageFee = roomSummary.rooms
    ? Math.round(roomSummary.feeTotal / roomSummary.rooms)
    : 0;

  // ================= LOAD ROOMS =================
  const loadRooms = async () => {
    try {
      setLoading(true);
      const res = await API.get(
        `/school-admin/hostels/${hostelId}/rooms/`,
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );
      setRooms(res.data.rooms || []);
    } catch (err) {
      console.error(err);
      alert("Failed to load rooms");
    } finally {
      setLoading(false);
    }
  };

  // ================= ADD ROOM =================
  const addRoom = async () => {
    if (!roomNumber.trim()) {
      alert("Room number is required");
      return;
    }

    if (!monthlyFee || Number(monthlyFee) <= 0) {
      alert("Enter valid monthly fee");
      return;
    }

    try {
      setSaving(true);
      await API.post(
        `/school-admin/hostels/${hostelId}/rooms/`,
        {
          room_number: roomNumber.trim(),
          total_beds: Number(totalBeds),
          monthly_fee: Number(monthlyFee),
        },
        { headers: { "X-SCHOOL-ID": school.school.id } }
      );

      setRoomNumber("");
      setTotalBeds(1);
      setMonthlyFee("");
      loadRooms();
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.error || "Failed to add room");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadRooms();
    // eslint-disable-next-line
  }, [hostelId]);

  return (
    <div className="hostel-page rooms-page">
      {/* HEADER */}
      <div className="rooms-hero">
        <div className="rooms-hero-left">
          <span className="rooms-hero-icon">🏨</span>
          <div>
            <h1>Hostel Rooms</h1>
            <p>
              Manage rooms, seater capacity & per-student monthly fee
            </p>
          </div>
        </div>

        <button
          className="rooms-back-btn"
          onClick={() => navigate("/school-admin/hostels")}
        >
          Back to Hostels
        </button>
      </div>

      <div className="rooms-stats">
        <div className="rooms-stat-card">
          <span>Total Rooms</span>
          <strong>{roomSummary.rooms}</strong>
        </div>
        <div className="rooms-stat-card">
          <span>Total Capacity</span>
          <strong>{roomSummary.beds}</strong>
        </div>
        <div className="rooms-stat-card">
          <span>Occupied / Free</span>
          <strong>{roomSummary.occupied} / {roomSummary.free}</strong>
        </div>
        <div className="rooms-stat-card">
          <span>Avg. Monthly Fee</span>
          <strong>₹{averageFee}</strong>
        </div>
      </div>

      {/* ADD ROOM */}
      <div className="rooms-add-panel">
        <div className="rooms-add-heading">
          <div>
            <h2>Add Room</h2>
            <p>Create capacity and monthly fee details for this hostel.</p>
          </div>
        </div>

        <div className="rooms-add-grid">
          <label className="rooms-field">
            <span>Room Number</span>
            <input
              placeholder="e.g. 101"
              value={roomNumber}
              onChange={(e) => setRoomNumber(e.target.value)}
            />
          </label>

          <label className="rooms-field">
            <span>Capacity</span>
            <select
              value={totalBeds}
              onChange={(e) => setTotalBeds(Number(e.target.value))}
            >
              <option value={1}>1 Seater</option>
              <option value={2}>2 Seater</option>
              <option value={3}>3 Seater</option>
              <option value={4}>4 Seater</option>
            </select>
          </label>

          <label className="rooms-field">
            <span>Monthly Fee per Student</span>
            <input
              type="number"
              placeholder="₹ amount"
              value={monthlyFee}
              onChange={(e) => setMonthlyFee(e.target.value)}
            />
          </label>

          <button
            onClick={addRoom}
            className="rooms-add-btn"
            disabled={saving}
          >
            {saving ? "Adding..." : "Add Room"}
          </button>
        </div>
      </div>

      <div className="rooms-section-header">
        <div>
          <h2>Room List</h2>
          <p>
            {loading
              ? "Loading rooms..."
              : `${rooms.length} room${rooms.length === 1 ? "" : "s"} available`}
          </p>
        </div>
      </div>

      {/* LOADING */}
      {loading && (
        <div className="rooms-loading">
          <div className="hostel-spinner" />
        </div>
      )}

      {/* ROOM LIST */}
      <div className="rooms-grid">
        {!loading && rooms.length === 0 && (
          <div className="rooms-empty">
            <span>🏨</span>
            <p>No rooms added yet</p>
            <small>Add the first room using the form above.</small>
          </div>
        )}

        {rooms.map((r) => {
          const totalBedsCount = Number(r.total_beds || 0);
          const occupiedBedsCount = Number(r.occupied_beds || 0);
          const freeBedsCount = Number(r.free_beds || Math.max(totalBedsCount - occupiedBedsCount, 0));
          const occupancy = totalBedsCount
            ? Math.min(100, Math.round((occupiedBedsCount / totalBedsCount) * 100))
            : 0;

          return (
          <div key={r.id} className="rooms-card">
            <div
              className="rooms-card-main"
              onClick={() =>
                navigate(`/school-admin/hostels/rooms/${r.id}/beds`)
              }
            >
              <div className="rooms-card-top">
                <div className="rooms-number">Room {r.room_number}</div>
                <span>{totalBedsCount} Seater</span>
              </div>

              <div className="rooms-card-fee">
                <span>Monthly Fee</span>
                <strong>₹{r.monthly_fee || 0}</strong>
                <small>per student</small>
              </div>

              <div className="rooms-occupancy">
                <div className="rooms-occupancy-row">
                  <span>Occupied {occupiedBedsCount} / {totalBedsCount}</span>
                  <span>{freeBedsCount} free</span>
                </div>
                <div className="rooms-progress">
                  <i style={{ width: `${occupancy}%` }} />
                </div>
              </div>
            </div>

            <div className="rooms-card-footer">
              <button
                className="rooms-manage-btn"
                onClick={() =>
                  navigate(`/school-admin/hostels/rooms/${r.id}/beds`)
                }
              >
                Manage Beds & Students
              </button>
            </div>
          </div>
          );
        })}
      </div>
    </div>
  );
}
